import apiService from "@/services/apiService";

export interface FtueSettings {
  ftue: boolean;
  seenLocationIds: string[];
  seenDeviceTypes: string[];
  seenFirstNewLocation: boolean;
}

export type FtueSettingsUpdate = Partial<FtueSettings>;

const DEFAULT_FTUE_SETTINGS: FtueSettings = {
  ftue: false,
  seenLocationIds: [],
  seenDeviceTypes: [],
  seenFirstNewLocation: false,
};

const FEATURE_TOUR_SETTING_KEY = "featureTour";
const OFFLINE_CACHE_KEY_PREFIX = "oneportal_ftue_settings_cache_";

const uniqueValues = (values: string[]): string[] =>
  [...new Set(values.filter(Boolean))];

/** In-memory only: the last value confirmed by the API this session. */
const lastKnownSettings = new Map<string, FtueSettings>();

/** Offline/failure fallback only — the API is always the source of truth when reachable. */
const readOfflineCache = (userId: string): FtueSettings | null => {
  try {
    const cached = localStorage.getItem(`${OFFLINE_CACHE_KEY_PREFIX}${userId}`);
    return cached ? (JSON.parse(cached) as FtueSettings) : null;
  } catch {
    return null;
  }
};

const writeOfflineCache = (userId: string, settings: FtueSettings): void => {
  try {
    localStorage.setItem(
      `${OFFLINE_CACHE_KEY_PREFIX}${userId}`,
      JSON.stringify(settings),
    );
  } catch {
    // Non-fatal — cache is best-effort only.
  }
};

const toFeatureTourValue = (settings: FtueSettings) => ({
  ftueCompleted: settings.ftue,
  seenLocationIds: settings.seenLocationIds,
  seenDeviceTypes: settings.seenDeviceTypes,
  seenFirstNewLocation: settings.seenFirstNewLocation,
});

const toFtueSettings = (value: unknown): FtueSettings => {
  try {
    const parsedSettings = (
      typeof value === "string" ? JSON.parse(value) : value
    ) as FtueSettingsUpdate | null;
    if (!parsedSettings || typeof parsedSettings !== "object") {
      return { ...DEFAULT_FTUE_SETTINGS };
    }

    return {
      ftue:
        parsedSettings.ftue ??
        (parsedSettings as FtueSettingsUpdate & { ftueCompleted?: boolean })
          .ftueCompleted ??
        DEFAULT_FTUE_SETTINGS.ftue,
      seenLocationIds: uniqueValues(parsedSettings.seenLocationIds ?? []),
      seenDeviceTypes: uniqueValues(parsedSettings.seenDeviceTypes ?? []),
      seenFirstNewLocation:
        parsedSettings.seenFirstNewLocation ??
        DEFAULT_FTUE_SETTINGS.seenFirstNewLocation,
    };
  } catch {
    return { ...DEFAULT_FTUE_SETTINGS };
  }
};

/**
 * Persists the full FTUE state in the authenticated user's featureTour app setting.
 * Falls back to a locally cached copy only when the API is unreachable.
 */
export const ftueSettingsService = {
  /** Synchronous read of the last API-confirmed settings, or null if none yet. */
  peek(userId: string): FtueSettings | null {
    return lastKnownSettings.get(userId) ?? null;
  },

  async get(userId: string): Promise<FtueSettings> {
    try {
      const appSettings = await apiService.getMyAppSettings();
      const settings = toFtueSettings(appSettings[FEATURE_TOUR_SETTING_KEY]);
      writeOfflineCache(userId, settings);
      lastKnownSettings.set(userId, settings);
      return settings;
    } catch (err) {
      const cached = readOfflineCache(userId);
      if (cached) {
        return cached;
      }
      throw err;
    }
  },

  async update(
    userId: string,
    update: FtueSettingsUpdate,
  ): Promise<FtueSettings> {
    const currentSettings = await this.get(userId);
    const nextSettings: FtueSettings = {
      ftue: update.ftue ?? currentSettings.ftue,
      seenLocationIds:
        update.seenLocationIds === undefined
          ? currentSettings.seenLocationIds
          : uniqueValues(update.seenLocationIds),
      seenDeviceTypes:
        update.seenDeviceTypes === undefined
          ? currentSettings.seenDeviceTypes
          : uniqueValues(update.seenDeviceTypes),
      seenFirstNewLocation:
        update.seenFirstNewLocation ?? currentSettings.seenFirstNewLocation,
    };

    try {
      await apiService.setMyAppSetting(
        FEATURE_TOUR_SETTING_KEY,
        toFeatureTourValue(nextSettings),
      );
    } catch {
      throw new Error("Failed to update FTUE app settings");
    }

    writeOfflineCache(userId, nextSettings);
    lastKnownSettings.set(userId, nextSettings);
    return nextSettings;
  },
};